import type { FormSchemaResolved } from '#layers/block-engine';
import type { OAuth2Config, OAuth2FormValues } from '../protocols/oauth2';
import { AuthProtocol, defineAuthProvider } from '../../../types/auth-providers/define';
import { oauth2 } from '../protocols/oauth2';
import {
	applyOAuth2PresetOptions,
	normalizeDomain,
	OIDC_CLAIM_MAPPINGS,
	OIDC_SCOPES,
	promoteOAuth2PresetFields,
} from './oauth2-preset';

interface OktaFormValues extends Omit<OAuth2FormValues, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	authorizationEndpoint: string;
}

const OKTA_DEFAULTS = {
	'scopes': OIDC_SCOPES,
	'claimMappings.sub': OIDC_CLAIM_MAPPINGS.sub,
	'claimMappings.email': OIDC_CLAIM_MAPPINGS.email,
} as const;

const OKTA_WEB_APP_REDIRECT_URL = 'https://developer.okta.com/docs/guides/sign-into-web-app-redirect/';

const OKTA_DESCRIPTIONS: Record<string, string> = {
	authorizationEndpoint: '$t:sso_providers.okta.domain_hint',
	clientId: '$t:sso_providers.okta.client_id_hint',
	clientSecret: '$t:sso_providers.okta.client_secret_hint',
};

function oktaEndpoints(domainInput: string): Pick<OAuth2Config, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	const domain = normalizeDomain(domainInput, '.okta.com');

	return {
		authorizationEndpoint: `https://${domain}/oauth2/v1/authorize`,
		tokenEndpoint: `https://${domain}/oauth2/v1/token`,
		userinfoEndpoint: `https://${domain}/oauth2/v1/userinfo`,
	};
}

function parseOktaDomain(config: unknown): Partial<OktaFormValues> {
	const parsed = oauth2.parseInitialValues?.(config as OAuth2Config) ?? {};
	const authEndpoint = (config as { authorizationEndpoint?: unknown })?.authorizationEndpoint;

	if (typeof authEndpoint !== 'string') return parsed;

	try {
		return {
			...parsed,
			authorizationEndpoint: new URL(authEndpoint).hostname,
		};
	}
	catch {
		// Malformed URL in saved config — fall back to base parse so the form
		// renders the raw value rather than crashing on edit.
		return parsed;
	}
}

function applyOktaDefaults(items: FormSchemaResolved<any>): FormSchemaResolved<any> {
	return promoteOAuth2PresetFields(applyOAuth2PresetOptions(items, {
		defaults: OKTA_DEFAULTS,
		descriptions: OKTA_DESCRIPTIONS,
		omitFieldNames: ['tokenEndpoint', 'userinfoEndpoint'],
		omitSectionLabels: ['$t:sso_providers.oauth2.section.endpoints'],
		fieldOverrides: {
			authorizationEndpoint: {
				label: '$t:sso_providers.okta.domain_label',
				required: true,
				props: { placeholder: '$t:sso_providers.okta.domain_placeholder' },
				default: () => '',
			},
		},
	}), ['authorizationEndpoint']);
}

export const okta = defineAuthProvider<typeof oauth2, OAuth2Config, OktaFormValues>({
	id: 'okta',
	name: '$t:sso_providers.okta.name',
	description: '$t:sso_providers.okta.description',
	protocol: AuthProtocol.OAuth2,
	icon: {
		default: 'logos:okta-icon',
		dark: 'simple-icons:okta',
	},
	docsUrl: OKTA_WEB_APP_REDIRECT_URL,
	walkthroughStepKeys: [
		{
			key: 'sso_providers.okta.walkthrough.create_app',
			href: OKTA_WEB_APP_REDIRECT_URL,
		},
		'sso_providers.okta.walkthrough.redirect',
		'sso_providers.okta.walkthrough.credentials',
	],
	extends: oauth2,
	options: ({ form, context }) =>
		form(applyOktaDefaults(context.base?.resolvedOptions ?? []) as never),
	transformOptions: (form): OAuth2Config =>
		oauth2.transformOptions!({
			...form,
			...oktaEndpoints(form.authorizationEndpoint),
		}),
	parseInitialValues: parseOktaDomain,
});
